import React from 'react'
import { FormattedMessage } from 'react-intl'
import BusinessSource from 'components/BusinessSource'
import Icon from 'components/Icon'
import { camelCase } from 'lodash'
import cx from 'classnames'
import styles from './ContactCard.scss'
import messages from './messages'

export interface IWeblinks {
  [index: string]: Array<string>
}

export type IContactType = 'weblinks' | 'phones' | 'emails' | 'addresses' | 'wallets'

export interface IContactCard {
  type: IContactType
  emails: Array<string>
  phones: Array<string>
  websiteLinks: IWeblinks
  isNFT: boolean
  isInfluencers: boolean
  addresses?: Array<string>
  wallets?: Array<string>
}

export default function ContactCard({
  type,
  websiteLinks,
  phones,
  emails,
  isNFT,
  addresses,
  isInfluencers,
  wallets,
}: IContactCard) {
  const getDisplayName = (key: string) => {
    if (key === 'gmaps' || key === 'googleMaps') return 'Google Maps'
    if (key === 'bbb') return 'BBB'
    if (key === 'angiesList' || key === 'angies') return "Angie's List"
    return key
  }

  const renderGroupedWebLink = (key: string, urls: Array<string>) => {
    const source = getDisplayName(key)
    const iconName = camelCase(source)
    const isSingle = urls.length === 1

    return (
      <article key={key} className={cx(styles.item, styles.sourceTile)}>
        <span className={styles.sourceIcon}>
          <BusinessSource source={iconName} isClickable />
        </span>
        <div className={styles.sourceContent}>
          <span className={styles.sourceName}>{source}</span>
          <div className={styles.linkPills}>
            {urls.map((url, index) => (
              <a
                key={`${key}-${index}`}
                href={url}
                target='_blank'
                rel='noreferrer'
                className={cx(styles.linkPill, { [styles.linkPillWide]: isSingle })}
                title={url}
              >
                {isSingle ? 'Open ↗' : index + 1}
              </a>
            ))}
          </div>
        </div>
      </article>
    )
  }

  const renderWebLinks = () =>
    Object.keys(websiteLinks)
      .filter(key => websiteLinks[key] && websiteLinks[key].length > 0)
      .map(key => renderGroupedWebLink(key, websiteLinks[key]))

  const renderItemContents = (contents: Array<string>) => {
    if (contents.length === 1) return contents[0]

    return contents
      .filter(element => !!element)
      .map((element, index) => (
        <li key={index} className={styles.listItem}>
          {element}
        </li>
      ))
  }

  return (
    <div
      className={cx(styles.container, { [styles.nftContainer]: isNFT, [styles.influencersContainer]: isInfluencers })}
    >
      <div className={styles.title}>
        {(type !== 'addresses' || (type === 'addresses' && isInfluencers)) && (
          <Icon name={type} size='regular' className={styles.icon}>
            <FormattedMessage {...messages[type]} />
          </Icon>
        )}
      </div>
      <div className={styles.inner}>
        <div className={styles.content}>
          {type === 'weblinks' && renderWebLinks()}
          {type === 'phones' && renderItemContents(phones)}
          {type === 'emails' && renderItemContents(emails)}
          {type === 'wallets' && wallets && renderItemContents(wallets)}
          {isInfluencers && addresses && type === 'addresses' && renderItemContents(addresses)}
        </div>
      </div>
    </div>
  )
}
